import {
  forwardRef,
  useEffect,
  useRef,
  useState,
  type ComponentProps,
  type ReactNode,
  type RefObject,
} from "react";
import { observer } from "mobx-react-lite";
import { toast } from "sonner";
import { ContextMenu, ContextMenuContent, ContextMenuTrigger } from "@/components/ui/context-menu";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useIssuesStore, usePMStore, useProjectMembersStore } from "@/stores/pm/hooks/usePMStore";
import { useProjects } from "@/domains/documents/hooks/useProjects";
import { useDocumentUpload } from "@/hooks/useDocumentUpload";
import { ISSUE_ATTACHMENTS_FOLDER_NAME } from "@/domains/documents/services/folderService";
import * as pmRepo from "@/stores/pm/repository";
import type { PMIssue, UserProfile } from "@/domains/pm/types";
import { friendlyIssueError } from "@/domains/pm/utils/issueErrors";
import { IssueMenuItems } from "./IssueMenuItems";
import { CONTEXT_MENU_KIT } from "./issueMenuKit";
import { useIssueMenuActions, commonIds, commonProjectId } from "./useIssueMenuActions";
import { ParentIssuePickerDialog } from "./ParentIssuePickerDialog";
import { IssueFormDialog } from "./IssueFormDialog";
import { IssueDocumentPicker } from "./IssueDocumentPicker";

const EMPTY_SELECTION: ReadonlySet<number> = new Set();

/**
 * Hidden file input wired to `useDocumentUpload`, exposed for the menu's
 * "Upload" item. Kept as its own component so the heavy upload hook is
 * only instantiated for an issue whose menu the user has actually opened
 * — not once per visible list row.
 */
const IssueUploadInput = forwardRef<HTMLInputElement, { issue: PMIssue; onComplete: () => void }>(
  function IssueUploadInput({ issue, onComplete }, ref) {
    const { handleFileUpload } = useDocumentUpload({
      buildingId: String(issue.building_id),
      projectId: String(issue.building_project_id),
      requireProject: true,
      storagePathSegments: ["issues", String(issue.id)],
      folderPathSegments: [ISSUE_ATTACHMENTS_FOLDER_NAME],
      onDocumentCreated: (documentId) => pmRepo.addIssueDocument(issue.id, documentId),
      onUploadComplete: onComplete,
    });

    const handleFiles = async (files: FileList | null) => {
      if (!files || files.length === 0) return;
      // Sequential — keeps the versioning RPC happy when files share a folder.
      for (const file of Array.from(files)) {
        await handleFileUpload(file);
      }
    };

    return (
      <input
        ref={ref}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => {
          handleFiles(e.target.files);
          e.target.value = "";
        }}
      />
    );
  }
);

interface IssueContextMenuProps {
  issue: PMIssue;
  /**
   * WHA-3428: the page's bulk-select set. When the right-clicked issue is
   * part of a multi-selection the menu acts on every selected issue.
   * Omitted on surfaces without multi-select (kanban).
   */
  selectedIssueIds?: ReadonlySet<number>;
  /** The row/card to attach the right-click trigger to. */
  children: ReactNode;
}

interface IssueMenuBodyProps {
  issue: PMIssue;
  selectedIssueIds: ReadonlySet<number>;
  saveSingle: (patch: Partial<PMIssue>) => Promise<void>;
  onChangeDescription: () => void;
  onMakeSubissue: () => void;
  onCreateSubissue: () => void;
  onAddFromFranklin: () => void;
  onUpload: () => void;
  /** "Add checklist" hands focus to the panel; the closing menu must not take it back. */
  keepFocusOnClose: () => void;
}

/**
 * The menu's items, and everything they need to know about the rest of the
 * list. Mounted only while the menu is open (Radix unmounts closed content):
 * working out the multi-selection, the subissues and the assignable users scans
 * the whole issue list, and doing that in every row and card on every change to
 * the list is what made the board and the list slow to respond.
 */
function assigneesFor(issue: PMIssue) {
  return (issue.assignees ?? []).map((assignee) => assignee.id);
}

function disciplinesFor(issue: PMIssue) {
  return (issue.disciplines ?? []).map((discipline) => discipline.id);
}

function selectionStatus(issue: PMIssue, targets: PMIssue[], multi: boolean) {
  if (!multi) return issue.status;
  const status = targets[0]?.status;
  return targets.every((target) => target.status === status) ? status : undefined;
}

function whenEditable(
  actions: ReturnType<typeof useIssueMenuActions>,
  issues: PMIssue[],
  action: () => void
) {
  return () => {
    if (actions.mayEdit(issues)) action();
  };
}

function useIssueMenuSelection(issue: PMIssue, selectedIssueIds: ReadonlySet<number>) {
  const issuesStore = useIssuesStore();
  const pmStore = usePMStore();
  const projectMembers = useProjectMembersStore();

  // Multi mode when the right-clicked issue is part of a >1 selection.
  const isMulti = selectedIssueIds.has(issue.id) && selectedIssueIds.size > 1;
  const targetIssues: PMIssue[] = isMulti
    ? issuesStore.issues.filter((i) => selectedIssueIds.has(i.id))
    : [issue];
  const buildingId = issue.building_id;

  // WHA-3455: an issue that already has subissues can't itself become a
  // subissue (one level of nesting only) — hide "Mark as subissue" for it.
  const hasSubissues = issuesStore.issues.some((i) => i.parent_issue_id === issue.id);

  const status = selectionStatus(issue, targetIssues, isMulti);
  const assigneeIds = isMulti ? commonIds(targetIssues.map(assigneesFor)) : assigneesFor(issue);
  const disciplineIds = isMulti
    ? commonIds(targetIssues.map(disciplinesFor))
    : disciplinesFor(issue);

  // WHA-3507: scope the assignee submenu to users with access to every
  // project represented in `targetIssues`. Single-issue right-clicks have
  // exactly one project; a multi-select shows only users assignable to
  // ALL selected issues' projects (intersection), mirroring the rule the
  // bulk-edit RLS would enforce on save anyway.
  const targetProjectIds = Array.from(
    new Set(targetIssues.map((i) => i.building_project_id))
  ).filter((id): id is number => Number.isFinite(id) && id > 0);
  const projectUsers: UserProfile[] = projectMembers.intersection(targetProjectIds);
  const targetProjectKey = targetProjectIds.join(",");

  // The pickers' data, read as the menu opens.
  useEffect(() => {
    pmStore.loadDisciplines();
    for (const pid of targetProjectKey.split(",")) {
      if (pid) projectMembers.fetch(Number(pid));
    }
  }, [pmStore, projectMembers, targetProjectKey]);

  // Move-to-project targets — the building's projects (issue #11). Single
  // right-clicks resolve to one current project; a mixed multi-selection
  // shows none as current.
  const { projects } = useProjects(String(buildingId));
  const projectOptions = projects.map((p) => ({
    id: Number(p.id),
    name: p.name,
    status: p.status,
  }));
  const currentProjectId = commonProjectId(targetIssues);

  return {
    issuesStore,
    pmStore,
    isMulti,
    targetIssues,
    buildingId,
    hasSubissues,
    status,
    assigneeIds,
    disciplineIds,
    projectUsers,
    projectOptions,
    currentProjectId,
  };
}

type MenuHandlers = Pick<
  ComponentProps<typeof IssueMenuItems>,
  | "onSetStatus"
  | "onAssigneesChange"
  | "onDisciplinesChange"
  | "onSetStartDate"
  | "onSetDueDate"
  | "onMoveToProject"
  | "onAskFranklin"
  | "onChangeDescription"
  | "onMakeSubissue"
  | "onRemoveFromParent"
  | "onCreateSubissue"
  | "onAddChecklist"
  | "onAddFromFranklin"
  | "onUpload"
>;

function issueMenuHandlers(
  props: IssueMenuBodyProps,
  selection: ReturnType<typeof useIssueMenuSelection>,
  actions: ReturnType<typeof useIssueMenuActions>
): MenuHandlers {
  const { issue } = props;
  const { targetIssues, buildingId, assigneeIds, disciplineIds, issuesStore } = selection;
  return {
    onSetStatus: (status) => actions.applyStatus(targetIssues, buildingId, status),
    onAssigneesChange: (ids) => actions.applyAssignees(targetIssues, buildingId, ids, assigneeIds),
    onDisciplinesChange: (ids) =>
      actions.applyDisciplines(targetIssues, buildingId, ids, disciplineIds),
    onSetStartDate: (date) => actions.applyStartDate(targetIssues, buildingId, date),
    onSetDueDate: (date) => actions.applyDueDate(targetIssues, buildingId, date),
    onMoveToProject: (id) => actions.applyMoveToProject(targetIssues, buildingId, id),
    onAskFranklin: () => actions.askFranklin(targetIssues),
    onChangeDescription: whenEditable(actions, targetIssues, props.onChangeDescription),
    onMakeSubissue: whenEditable(actions, [issue], props.onMakeSubissue),
    onRemoveFromParent: whenEditable(actions, [issue], () => {
      void props.saveSingle({ parent_issue_id: null });
    }),
    onCreateSubissue: whenEditable(actions, [issue], props.onCreateSubissue),
    onAddChecklist: whenEditable(actions, [issue], () => {
      props.keepFocusOnClose();
      if (issuesStore.selectedIssue?.id !== issue.id) issuesStore.selectIssue(issue.id);
      issuesStore.requestChecklist(issue.id);
    }),
    onAddFromFranklin: whenEditable(actions, [issue], props.onAddFromFranklin),
    onUpload: whenEditable(actions, [issue], props.onUpload),
  };
}

const IssueMenuBody = observer(function IssueMenuBody(props: IssueMenuBodyProps) {
  const { issue, selectedIssueIds } = props;
  const selection = useIssueMenuSelection(issue, selectedIssueIds);
  const {
    pmStore,
    isMulti,
    targetIssues,
    hasSubissues,
    status,
    assigneeIds,
    disciplineIds,
    projectUsers,
    projectOptions,
    currentProjectId,
  } = selection;
  const actions = useIssueMenuActions();
  return (
    <IssueMenuItems
      kit={CONTEXT_MENU_KIT}
      mode={isMulti ? "multi" : "single"}
      selectedCount={targetIssues.length}
      status={status}
      assigneeIds={assigneeIds}
      disciplineIds={disciplineIds}
      startDate={isMulti ? undefined : issue.start_date}
      dueDate={isMulti ? undefined : issue.due_date}
      projects={projectOptions}
      currentProjectId={currentProjectId}
      hasParent={issue.parent_issue_id != null}
      hasSubissues={hasSubissues}
      users={projectUsers}
      disciplines={pmStore.disciplines}
      {...issueMenuHandlers(props, selection, actions)}
    />
  );
});

/**
 * WHA-3428 — right-click context menu for a PM issue. Single-issue mode
 * exposes the full menu; when the right-clicked issue is inside a
 * multi-selection the menu switches to bulk mode and applies to every
 * selected issue. Reuses `IssueMenuItems` (shared with the toolbar's
 * Actions dropdown) and the existing edit dialogs.
 *
 * Every list row and board card wraps itself in one of these, so what it
 * renders while closed is paid once per issue on every re-render of the list:
 * it renders the trigger and nothing else until its menu is first opened.
 */
function useIssueContextState(issue: PMIssue) {
  const { t } = useLanguage();
  const issuesStore = useIssuesStore();
  const [hasOpened, setHasOpened] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [titleDraft, setTitleDraft] = useState(issue.title);
  const [parentPickerOpen, setParentPickerOpen] = useState(false);
  const [createSubOpen, setCreateSubOpen] = useState(false);
  const [docPickerOpen, setDocPickerOpen] = useState(false);
  const buildingId = issue.building_id;
  const saveSingle = async (patch: Partial<PMIssue>) => {
    try {
      await issuesStore.bulkUpdate([issue.id], patch, buildingId);
      if (issuesStore.selectedIssue?.id === issue.id) issuesStore.selectIssue(issue.id);
    } catch (error) {
      toast.error(friendlyIssueError(error, t));
    }
  };
  const refreshIssue = () => {
    issuesStore.loadIssues(buildingId);
    if (issuesStore.selectedIssue?.id === issue.id) issuesStore.selectIssue(issue.id);
  };
  const saveRename = () => {
    const next = titleDraft.trim();
    setRenameOpen(false);
    if (next && next !== issue.title) void saveSingle({ title: next });
  };
  return {
    hasOpened,
    setHasOpened,
    renameOpen,
    setRenameOpen,
    titleDraft,
    setTitleDraft,
    parentPickerOpen,
    setParentPickerOpen,
    createSubOpen,
    setCreateSubOpen,
    docPickerOpen,
    setDocPickerOpen,
    saveSingle,
    refreshIssue,
    saveRename,
  };
}

type IssueContextState = ReturnType<typeof useIssueContextState>;

function IssueRenameDialog({ state }: { state: IssueContextState }) {
  const { t } = useLanguage();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = "auto";
    textarea.style.height = `${textarea.scrollHeight}px`;
  }, [state.titleDraft, state.renameOpen]);
  return (
    <Dialog open={state.renameOpen} onOpenChange={state.setRenameOpen}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t.pm.issues.contextMenu.changeDescription}</DialogTitle>
        </DialogHeader>
        <Textarea
          ref={textareaRef}
          value={state.titleDraft}
          onChange={(event) => state.setTitleDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              state.saveRename();
            }
          }}
          rows={3}
          autoFocus
          className="max-h-[50vh] resize-none overflow-y-auto"
        />
        <DialogFooter>
          <Button variant="outline" onClick={() => state.setRenameOpen(false)}>
            {t.pm.common.cancel}
          </Button>
          <Button onClick={state.saveRename} disabled={!state.titleDraft.trim()}>
            {t.pm.common.save}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function IssueContextExtras({
  issue,
  state,
  uploadInputRef,
}: {
  issue: PMIssue;
  state: IssueContextState;
  uploadInputRef: RefObject<HTMLInputElement>;
}) {
  return (
    <>
      <IssueUploadInput ref={uploadInputRef} issue={issue} onComplete={state.refreshIssue} />
      <IssueRenameDialog state={state} />
      <ParentIssuePickerDialog
        open={state.parentPickerOpen}
        onOpenChange={state.setParentPickerOpen}
        buildingId={issue.building_id}
        currentIssueId={issue.id}
        onPicked={(parentId) => state.saveSingle({ parent_issue_id: parentId })}
      />
      <IssueFormDialog
        open={state.createSubOpen}
        onOpenChange={state.setCreateSubOpen}
        buildingId={issue.building_id}
        defaultProjectId={String(issue.building_project_id)}
        parentIssueId={issue.id}
      />
      <IssueDocumentPicker
        open={state.docPickerOpen}
        onOpenChange={state.setDocPickerOpen}
        issueId={issue.id}
        buildingId={String(issue.building_id)}
        projectId={String(issue.building_project_id)}
        alreadyAttachedIds={(issue.linked_documents ?? []).map((document) => String(document.id))}
        onAttached={state.refreshIssue}
      />
    </>
  );
}

export const IssueContextMenu = observer(function IssueContextMenu({
  issue,
  selectedIssueIds = EMPTY_SELECTION,
  children,
}: IssueContextMenuProps) {
  const state = useIssueContextState(issue);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const keepFocusOnClose = useRef(false);
  return (
    <>
      <ContextMenu
        onOpenChange={(open) => {
          if (open) state.setHasOpened(true);
        }}
      >
        <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
        <ContextMenuContent
          className="w-56"
          onCloseAutoFocus={(event) => {
            if (!keepFocusOnClose.current) return;
            keepFocusOnClose.current = false;
            event.preventDefault();
          }}
        >
          <IssueMenuBody
            issue={issue}
            selectedIssueIds={selectedIssueIds}
            saveSingle={state.saveSingle}
            onChangeDescription={() => {
              state.setTitleDraft(issue.title);
              state.setRenameOpen(true);
            }}
            onMakeSubissue={() => state.setParentPickerOpen(true)}
            onCreateSubissue={() => state.setCreateSubOpen(true)}
            onAddFromFranklin={() => state.setDocPickerOpen(true)}
            onUpload={() => uploadInputRef.current?.click()}
            keepFocusOnClose={() => {
              keepFocusOnClose.current = true;
            }}
          />
        </ContextMenuContent>
      </ContextMenu>
      {state.hasOpened && (
        <IssueContextExtras issue={issue} state={state} uploadInputRef={uploadInputRef} />
      )}
    </>
  );
});
